import { useEffect, useState } from "react";
import Button from "../ui/Button.jsx";

const MODULES = [
  { id: "kratos", label: "Kratos" },
  { id: "hydra", label: "Hydra" },
  { id: "keto", label: "Keto" },
  { id: "oathkeeper", label: "Oathkeeper" },
  { id: "polis", label: "Polis" },
];

/**
 * Required scope + completeness attestation before self-hosted Ory assessment runs.
 */
export default function OryAttestationGate({ projectId, onComplete, onCancel }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [modules, setModules] = useState({
    kratos: true,
    hydra: false,
    keto: false,
    oathkeeper: false,
    polis: false,
  });
  const [configCompleteness, setConfigCompleteness] = useState("complete");
  const [note, setNote] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/ory-attestation`, {
          credentials: "include",
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || "Failed to load attestation status");
        if (cancelled) return;
        setInventory(data.inventory || null);
        if (data.attestation?.modules_in_scope) {
          setModules({ ...modules, ...data.attestation.modules_in_scope });
        }
        if (data.attestation?.config_completeness) {
          setConfigCompleteness(data.attestation.config_completeness);
        }
        if (data.attestation_present && !data.attestation_stale) {
          onComplete?.();
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per project
  }, [projectId]);

  const toggleModule = (id) => {
    setModules((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/ory-attestation`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          modules_in_scope: modules,
          config_completeness: configCompleteness,
          note,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to save attestation");
      onComplete?.();
    } catch (err) {
      setError(err?.message || "Failed to save attestation");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <p className="text-secondary" style={{ fontSize: "14px" }}>
        Loading deployment inventory…
      </p>
    );
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} style={{ maxWidth: "520px" }}>
      <h2 style={{ fontSize: "20px", fontWeight: 600, marginBottom: "8px" }}>
        Confirm assessment scope
      </h2>
      <p className="text-secondary" style={{ fontSize: "13px", lineHeight: 1.5, marginBottom: "16px" }}>
        Before we run, declare which Ory modules this deployment actually runs and whether the configuration
        you provided is complete. This keeps absent modules as clean N/A — not silent gaps.
      </p>

      {inventory?.summaryLines?.length ? (
        <div
          className="mb-4 rounded-md border p-3 text-[12px] leading-relaxed"
          style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}
        >
          <p className="mb-2 font-semibold" style={{ color: "var(--text-secondary)" }}>
            Input inventory
          </p>
          <ul className="m-0 list-disc pl-4">
            {inventory.summaryLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <fieldset style={{ border: "none", padding: 0, margin: "0 0 16px" }}>
        <legend className="text-secondary" style={{ fontSize: "12px", marginBottom: "8px" }}>
          Modules in scope (attested)
        </legend>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {MODULES.map((m) => (
            <label
              key={m.id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "13px",
                padding: "6px 10px",
                borderRadius: "6px",
                border: "1px solid var(--border)",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={Boolean(modules[m.id])}
                onChange={() => toggleModule(m.id)}
              />
              {m.label}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset style={{ border: "none", padding: 0, margin: "0 0 16px" }}>
        <legend className="text-secondary" style={{ fontSize: "12px", marginBottom: "8px" }}>
          Configuration completeness (attested)
        </legend>
        <label style={{ display: "flex", gap: "8px", fontSize: "13px", marginBottom: "6px" }}>
          <input
            type="radio"
            name="configCompleteness"
            value="complete"
            checked={configCompleteness === "complete"}
            onChange={() => setConfigCompleteness("complete")}
          />
          Complete for modules in scope
        </label>
        <label style={{ display: "flex", gap: "8px", fontSize: "13px" }}>
          <input
            type="radio"
            name="configCompleteness"
            value="partial"
            checked={configCompleteness === "partial"}
            onChange={() => setConfigCompleteness("partial")}
          />
          Partial — some settings may not be represented in uploaded config
        </label>
      </fieldset>

      <label style={{ display: "grid", gap: "6px", fontSize: "13px", marginBottom: "16px" }}>
        <span className="text-secondary">Optional note</span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="e.g. Hydra runs in a separate cluster — URL pending"
          style={{
            padding: "8px 10px",
            borderRadius: "6px",
            border: "1px solid var(--border)",
            background: "var(--bg-input)",
            color: "var(--text-primary)",
          }}
        />
      </label>

      {error ? (
        <p className="text-critical" style={{ fontSize: "13px", marginBottom: "12px" }}>
          {error}
        </p>
      ) : null}

      <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        <Button type="submit" disabled={submitting || !Object.values(modules).some(Boolean)}>
          {submitting ? "Saving…" : "Confirm & run assessment"}
        </Button>
      </div>
    </form>
  );
}
